import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarDays, DollarSign, Users, Building2, Clock, Check, AlertCircle, BarChart3, X, Pencil, List } from "lucide-react";
import { EditBookingDialog } from "@/components/EditBookingDialog";
import { BookingsCalendar } from "@/components/BookingsCalendar";

export default function AdminDashboard() {
  const { api, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editBooking, setEditBooking] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  useEffect(() => {
    if (!isAdmin) { navigate("/login"); return; }
    loadData();
  }, [isAdmin]);

  const loadData = async () => {
    try {
      const [statsRes, bookingsRes] = await Promise.all([
        api("get", "/admin/stats"),
        api("get", "/bookings")
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data.slice(0, 20));
    } catch (err) {
      toast.error("Failed to load data");
    } finally { setLoading(false); }
  };

  const confirmPayment = async (bookingId) => {
    try {
      await api("post", `/payments/${bookingId}/confirm`, { method: "cash" });
      toast.success("Payment confirmed");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to confirm");
    }
  };

  const updateStatus = async (bookingId, status) => {
    try {
      await api("put", `/bookings/${bookingId}/status`, { status });
      toast.success(`Status updated to ${status}`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const approveCancellation = async (bookingId) => {
    try {
      await api("put", `/admin/bookings/${bookingId}/approve-cancellation`);
      toast.success("Cancellation approved");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const denyCancellation = async (bookingId) => {
    try {
      await api("put", `/admin/bookings/${bookingId}/deny-cancellation`);
      toast.success("Cancellation denied — booking remains active");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const statusBadge = (status) => {
    const colors = {
      confirmed: "bg-emerald-100 text-emerald-700",
      pending_payment: "bg-amber-100 text-amber-700",
      pending_cancellation: "bg-orange-100 text-orange-700",
      cancelled: "bg-red-100 text-red-700",
      completed: "bg-blue-100 text-blue-700",
      no_show: "bg-gray-100 text-gray-700",
    };
    const labels = { pending_cancellation: "cancel requested" };
    return <Badge className={`${colors[status] || "bg-gray-100 text-gray-700"} text-xs`}>{labels[status] || status?.replace(/_/g, " ")}</Badge>;
  };

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-12"><div className="h-64 bg-muted animate-pulse rounded-xl" /></div>;

  const statCards = [
    { label: "Total Bookings", value: stats?.total_bookings || 0, icon: CalendarDays, color: "text-primary" },
    { label: "Active Bookings", value: stats?.active_bookings || 0, icon: Check, color: "text-emerald-600" },
    { label: "Pending Payments", value: stats?.pending_payments || 0, icon: AlertCircle, color: "text-amber-600" },
    { label: "Active Members", value: stats?.total_members || 0, icon: Users, color: "text-blue-600" },
    { label: "Total Spaces", value: stats?.total_spaces || 0, icon: Building2, color: "text-secondary" },
    { label: "Revenue", value: `XCG ${(stats?.total_revenue || 0).toLocaleString()}`, icon: DollarSign, color: "text-primary" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 animate-fade-in" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl">Admin Dashboard</h1>
          <p className="text-muted-foreground mt-1">Manage bookings, spaces, and members</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/admin/reports")} data-testid="admin-reports-btn">
            <BarChart3 className="h-4 w-4 mr-2" />Reports
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" data-testid="admin-stats-grid">
        {statCards.map((s, i) => (
          <Card key={i} className="p-4 border border-border" data-testid={`stat-card-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              <s.icon className={`h-4 w-4 ${s.color}`} />
              <span className="text-xs text-muted-foreground">{s.label}</span>
            </div>
            <p className="font-heading font-bold text-2xl">{s.value}</p>
          </Card>
        ))}
      </div>

      {/* Quick Navigation */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mb-8">
        <Card className="p-4 border border-border cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate("/admin/spaces")} data-testid="quick-nav-spaces">
          <Building2 className="h-5 w-5 text-primary mb-2" />
          <p className="font-medium">Manage Spaces</p>
          <p className="text-xs text-muted-foreground">Add, edit, or remove spaces</p>
        </Card>
        <Card className="p-4 border border-border cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate("/admin/members")} data-testid="quick-nav-members">
          <Users className="h-5 w-5 text-primary mb-2" />
          <p className="font-medium">Manage Members</p>
          <p className="text-xs text-muted-foreground">View and manage member profiles</p>
        </Card>
        <Card className="p-4 border border-border cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate("/admin/reports")} data-testid="quick-nav-reports">
          <BarChart3 className="h-5 w-5 text-primary mb-2" />
          <p className="font-medium">Reports</p>
          <p className="text-xs text-muted-foreground">Billing, revenue, utilization</p>
        </Card>
        <Card className="p-4 border border-border cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate("/admin/cms")} data-testid="quick-nav-cms">
          <Building2 className="h-5 w-5 text-primary mb-2" />
          <p className="font-medium">Site Management</p>
          <p className="text-xs text-muted-foreground">Edit homepage, SEO & GEO</p>
        </Card>
        <Card className="p-4 border border-border cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate("/admin/managers")} data-testid="quick-nav-managers">
          <Users className="h-5 w-5 text-primary mb-2" />
          <p className="font-medium">Managers & Messages</p>
          <p className="text-xs text-muted-foreground">Manage business managers</p>
        </Card>
      </div>

      {/* Recent Bookings */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-semibold text-xl">Recent Bookings</h2>
        <div className="flex gap-1 border rounded-lg p-0.5" data-testid="admin-view-toggle">
          <Button
            variant={viewMode === "list" ? "default" : "ghost"}
            size="sm"
            className={`h-7 px-2.5 rounded-md ${viewMode === "list" ? "bg-foreground text-background" : ""}`}
            onClick={() => setViewMode("list")}
            data-testid="admin-list-view"
          >
            <List className="h-3.5 w-3.5 mr-1" />List
          </Button>
          <Button
            variant={viewMode === "calendar" ? "default" : "ghost"}
            size="sm"
            className={`h-7 px-2.5 rounded-md ${viewMode === "calendar" ? "bg-foreground text-background" : ""}`}
            onClick={() => setViewMode("calendar")}
            data-testid="admin-calendar-view"
          >
            <CalendarDays className="h-3.5 w-3.5 mr-1" />Calendar
          </Button>
        </div>
      </div>

      {viewMode === "calendar" ? (
        <BookingsCalendar
          bookings={bookings}
          calMonth={calMonth}
          setCalMonth={setCalMonth}
          statusColor={(s) => s === "confirmed" ? "bg-emerald-100 text-emerald-700" : s === "cancelled" ? "bg-gray-100 text-gray-500" : "bg-amber-100 text-amber-700"}
          onEdit={setEditBooking}
          onCancel={(id) => updateStatus(id, "cancelled")}
        />
      ) : (
      <div className="rounded-lg border overflow-x-auto" data-testid="admin-bookings-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Space</TableHead>
              <TableHead>Booked By</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Time</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bookings.map(b => (
              <TableRow key={b.id}>
                <TableCell>
                  <div className="font-medium">{b.space_name}</div>
                  {b.meeting_name && <div className="text-xs text-muted-foreground">{b.meeting_name}</div>}
                </TableCell>
                <TableCell>{b.user_name || b.guest_name || "—"}{b.booked_for_name && <><br /><span className="text-xs text-rig-teal">For: {b.booked_for_name}</span></>}<br /><span className="text-xs text-muted-foreground">{b.user_email || b.guest_email || ""}</span></TableCell>
                <TableCell className="font-mono text-xs">{b.start_datetime?.slice(0, 10)}</TableCell>
                <TableCell className="font-mono text-xs">{b.start_datetime?.slice(11, 16)} - {b.end_datetime?.slice(11, 16)}</TableCell>
                <TableCell>{statusBadge(b.status)}</TableCell>
                <TableCell className="font-medium">XCG {b.price?.toFixed(2)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {b.status === "pending_payment" && (
                      <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 text-xs" onClick={() => confirmPayment(b.id)} data-testid={`confirm-payment-${b.id}`}>
                        <Check className="h-3 w-3 mr-1" />Confirm
                      </Button>
                    )}
                    {b.status === "pending_cancellation" && (
                      <>
                        <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 text-xs" onClick={() => approveCancellation(b.id)} data-testid={`approve-cancel-${b.id}`}>
                          <Check className="h-3 w-3 mr-1" />Approve
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 text-xs" onClick={() => denyCancellation(b.id)} data-testid={`deny-cancel-${b.id}`}>
                          <X className="h-3 w-3 mr-1" />Deny
                        </Button>
                      </>
                    )}
                    {!["cancelled", "completed", "no_show"].includes(b.status) && (
                      <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-foreground" onClick={() => setEditBooking(b)} data-testid={`edit-booking-${b.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {!["pending_cancellation"].includes(b.status) && (
                      <Select onValueChange={(val) => updateStatus(b.id, val)}>
                        <SelectTrigger className="h-8 w-28 text-xs" data-testid={`status-select-${b.id}`}><SelectValue placeholder="Update" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="confirmed">Confirm</SelectItem>
                          <SelectItem value="cancelled">Cancel</SelectItem>
                          <SelectItem value="completed">Complete</SelectItem>
                          <SelectItem value="no_show">No Show</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {bookings.length === 0 && (
          <div className="p-12 text-center text-muted-foreground">No bookings yet</div>
        )}
      </div>
      )}
      <EditBookingDialog
        booking={editBooking}
        open={!!editBooking}
        onClose={() => setEditBooking(null)}
        api={api}
        onSaved={loadData}
      />
    </div>
  );
}
